import { useEffect, useState } from 'react';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Select } from '@/shared/components/ui/Select';
import { STATUS_OPTIONS } from '../constants';
import type {
  CreateMeetingPayload,
  Meeting,
  MeetingStatus,
  UpdateMeetingPayload,
} from '../types';

interface MeetingFormModalProps {
  open: boolean;
  meeting?: Meeting | null; // null / undefined = create mode
  loading?: boolean;
  onClose: () => void;
  onCreate: (payload: CreateMeetingPayload) => void;
  onUpdate: (id: string, payload: UpdateMeetingPayload) => void;
}

type FormState = {
  title: string;
  meeting_date: string;
  meeting_time: string;
  meeting_link: string;
  status: MeetingStatus;
};

function todayISO(): string {
  return new Date().toISOString().slice(0, 10);
}

const EMPTY: FormState = {
  title: '',
  meeting_date: todayISO(),
  meeting_time: '10:00',
  meeting_link: '',
  status: 'scheduled',
};

export function MeetingFormModal({
  open,
  meeting,
  loading,
  onClose,
  onCreate,
  onUpdate,
}: MeetingFormModalProps) {
  const isEdit = !!meeting;
  const [form, setForm] = useState<FormState>(EMPTY);

  useEffect(() => {
    if (!open) return;
    if (meeting) {
      setForm({
        title: meeting.title ?? '',
        meeting_date: meeting.meeting_date ?? todayISO(),
        meeting_time: meeting.meeting_time ?? '10:00',
        meeting_link: meeting.link ?? '',
        status: meeting.status ?? 'scheduled',
      });
    } else {
      setForm({ ...EMPTY, meeting_date: todayISO() });
    }
  }, [open, meeting]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;

    if (isEdit && meeting) {
      // Backend only persists title + status; nothing else is sent.
      onUpdate(meeting.id, { title: form.title.trim(), status: form.status });
    } else {
      onCreate({
        title: form.title.trim(),
        meeting_date: form.meeting_date,
        meeting_time: form.meeting_time,
        meeting_link: form.meeting_link.trim() || undefined,
      });
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Edit Meeting' : 'New Meeting'}
      size="md"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button type="submit" form="meeting-form" loading={loading}>
            {isEdit ? 'Save Changes' : 'Create Meeting'}
          </Button>
        </>
      }
    >
      <form id="meeting-form" onSubmit={handleSubmit} className="space-y-4">
        {/* Title — always editable */}
        <Input
          label="Title *"
          name="meeting-title"
          value={form.title}
          onChange={(e) => set('title', e.target.value)}
          placeholder="Meeting title…"
          required
        />

        {isEdit ? (
          /* Edit: only status (title above). Date/time/link omitted — backend ignores them. */
          <Select
            label="Status"
            name="meeting-status"
            value={form.status}
            onChange={(e) => set('status', e.target.value as MeetingStatus)}
            options={STATUS_OPTIONS}
          />
        ) : (
          /* Create: date, time, join link */
          <>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Date *"
                name="meeting-date"
                type="date"
                value={form.meeting_date}
                onChange={(e) => set('meeting_date', e.target.value)}
                required
              />
              <Input
                label="Time"
                name="meeting-time"
                type="time"
                value={form.meeting_time}
                onChange={(e) => set('meeting_time', e.target.value)}
              />
            </div>
            <Input
              label="Join Link"
              name="meeting-link"
              type="url"
              value={form.meeting_link}
              onChange={(e) => set('meeting_link', e.target.value)}
              placeholder="https://…"
            />
          </>
        )}
      </form>
    </Modal>
  );
}
